import {Injectable} from '@angular/core';
import {SavedPositionsService} from "./saved-positions.service";
import {CashFlowStrategyTemplatesService, LastUsedTemplate} from "./cashflow-strategy-templates.service";
import {ApgPortfolio} from "../model/ApgPortfolio";
import {BeforePositionDto} from "../model/BeforePositionDto";
import {isVoid} from "../../utils";
import {OptionTicker, parseOptionTicker} from "../../options-common/options.model";
import {PositionsStateService} from "./positions-before-state.service";

@Injectable()
export class ApgDataService {

    constructor(
        private readonly _cashFlowPositionsService: SavedPositionsService,
        private readonly _cashFlowTemplatesService: CashFlowStrategyTemplatesService,
        private readonly _apgPositionsStateService: PositionsStateService,
    ) {
    }



    async getPortfolioPositions(portfolio: ApgPortfolio): Promise<BeforePositionDto[][]> {

        // Independent HG portfolios (CR-20260606-001) have no APG positions.
        if (portfolio?.isIndependent) {
            return [];
        }

        const serviceConfig = {
            orientation: undefined,
            userId: portfolio.userId,
            userName: portfolio.userName
        };

        const source = await this.resolvePositionsSource(portfolio, serviceConfig);

        const positions = this._cashFlowPositionsService.getPositions(
            portfolio.id,
            source.underlying,
            source.strategy
        );

        return positions;
    }

    /**
     * The user-settings key the portfolio's saved positions live under — the SAME key
     * getPortfolioPositions reads, resolved the same way (CR-20260916-004: Maven watches the
     * shell's settings echo for this key to tell the trader the positions changed since his run).
     * Null for an independent HG portfolio, which has no APG positions.
     */
    async getPortfolioPositionsKey(portfolio: ApgPortfolio): Promise<string | null> {

        if (portfolio?.isIndependent) {
            return null;
        }

        const serviceConfig = {
            orientation: undefined,
            userId: portfolio.userId,
            userName: portfolio.userName
        };

        const source = await this.resolvePositionsSource(portfolio, serviceConfig);

        return this._cashFlowPositionsService.makeStorageKey(
            portfolio.id,
            source.underlying,
            source.strategy
        );
    }

    // Which (underlying, strategy) pair the saved positions are keyed by: the portfolio's
    // last-used template, or the portfolio's own pair when no last-used template is recorded.
    // Configures both services for the portfolio's OWNER first, as every read here does.
    private async resolvePositionsSource(
        portfolio: ApgPortfolio,
        serviceConfig: { orientation: undefined; userId: string; userName: string }
    ): Promise<{ underlying: string; strategy: ApgPortfolio['strategy'] }> {

        this._cashFlowPositionsService.configure(serviceConfig);

        await this._cashFlowTemplatesService.configure(serviceConfig);

        const lastUsedTemplate = this._cashFlowTemplatesService
            .getLastUsedTemplate(portfolio.id);

        if (isVoid(lastUsedTemplate)) {
            console.error('Failed to detect last used template. Positions loaded based on best effort');
            return {underlying: portfolio.asset, strategy: portfolio.strategy};
        }

        return {underlying: lastUsedTemplate.underlying, strategy: lastUsedTemplate.strategy};
    }

    // CR-20260928-001: every portfolio's underlying is FIXED and stored on the portfolio. The
    // retired '----' scratchpad was the one whose underlying had to be read from its last-used
    // template; that branch (and its never-invalidated cache) is gone. Kept async for its callers.
    async getUnderlyingOfPortfolio(portfolio: ApgPortfolio): Promise<string> {
        return isVoid(portfolio) ? undefined : portfolio.asset;
    }

    async getLastUsedTemplateOfPortfolio(portfolio: ApgPortfolio) : Promise<LastUsedTemplate> {

        const serviceConfig = {
            orientation: undefined,
            userId: portfolio.userId,
            userName: portfolio.userName
        };

        await this._cashFlowTemplatesService.configure(serviceConfig);

        const lastUsedTemplate = this._cashFlowTemplatesService
            .getLastUsedTemplate(portfolio.id);

        return lastUsedTemplate;
    }

    async getShortOptionTicker(portfolio: ApgPortfolio): Promise<OptionTicker> {
        const positions = await this.getPortfolioPositions(portfolio);

        if (isVoid(positions)) {
            return undefined;
        }

        const side = positions[0];

        if (isVoid(side)) {
            return undefined;
        }

        const beforePositionDto = side.find(x => x.role === 'ShortOption');

        if (isVoid(beforePositionDto)) {
            return undefined;
        }

        const optionTicker = parseOptionTicker(beforePositionDto.ticker);

        return optionTicker;
    }

    async getDefaultQtyForPortfolio(args: ApgPortfolio) : Promise<number> {

        // Independent HG portfolios (CR-20260606-001) carry their own default qty;
        // they have no APG template to derive it from (the lut.strategy deref below
        // would throw on the missing template).
        if (args?.isIndependent) {
            return isVoid(args.defaultQty) ? 1 : args.defaultQty;
        }

        const underlyingOfPortfolio = await this.getUnderlyingOfPortfolio(args);

        const lut = await this.getLastUsedTemplateOfPortfolio(args);

        const serviceConfig = {
            orientation: undefined,
            userId: args.userId,
            userName: args.userName
        };

        this._apgPositionsStateService.configure(serviceConfig);

        const dp = this._apgPositionsStateService.getDefaultsProvider(
            args.id,
            underlyingOfPortfolio,
            lut.strategy === 'Calls & Puts' ? 'double' : 'single'
        );

        return dp?.first?.orderQty;
    }

}